import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaBriefcase, FaUser, FaTimes } from 'react-icons/fa';

const EmailDemo = () => {
  const [emails, setEmails] = useState([]);
  const [showDemo, setShowDemo] = useState(false);

  useEffect(() => {
    // Listen for demo emails (in real app, this would be from a backend)
    const handleEmailDemo = (event) => {
      if (event.detail) {
        const newEmail = {
          id: Date.now(),
          ...event.detail,
          timestamp: new Date().toLocaleString()
        };
        setEmails(prev => [newEmail, ...prev]);
      }
    };

    window.addEventListener('emailDemo', handleEmailDemo);
    return () => window.removeEventListener('emailDemo', handleEmailDemo);
  }, []);

  // Add demo emails for testing
  useEffect(() => {
    const demoEmails = [
      {
        id: 1,
        type: 'hire',
        name: 'Sarah Johnson',
        company: 'TechCorp Inc.',
        email: 'sarah.johnson@techcorp.com',
        message: 'We are looking for a Front End Developer for our new project. The role involves React, PHP, and API integration. Salary range: $60k-80k. Remote work available.',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toLocaleString()
      },
    ];
    setEmails(demoEmails);
  }, []);

  if (!showDemo) {
    return (
      <button
        onClick={() => setShowDemo(true)}
        className="fixed bottom-20 right-8 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all z-40"
        title="View Demo Emails"
      >
        <FaEnvelope size={20} />
        {emails.length > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
            {emails.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">📧 Email Demo Dashboard</h2>
            <p className="text-blue-100">Received emails from your portfolio</p>
          </div>
          <button
            onClick={() => setShowDemo(false)}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Email List */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {emails.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FaEnvelope size={48} className="mx-auto mb-4 opacity-50" />
              <p>No emails received yet</p>
              <p className="text-sm">Try submitting the contact form or hire me popup!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {emails.map((email) => (
                <div
                  key={email.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      {email.type === 'hire' ? (
                        <FaBriefcase className="text-green-600 mr-2" />
                      ) : (
                        <FaUser className="text-blue-600 mr-2" />
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-800">
                          {email.name}
                          {email.company && (
                            <span className="text-gray-600 font-normal"> from {email.company}</span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-500">{email.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        email.type === 'hire' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {email.type === 'hire' ? '💼 Hiring' : '💬 Contact'}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">{email.timestamp}</p>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">{email.message}</p>
                  </div>
                  
                  <div className="mt-3 flex space-x-2">
                    <a
                      href={`mailto:${email.email}?subject=Re: ${email.type === 'hire' ? 'Your hiring inquiry' : 'Your message'}`}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                    >
                      Reply via Email
                    </a>
                    <button className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm transition-colors">
                      Mark as Read
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <p className="text-sm text-gray-600 text-center">
            📝 This is a demo dashboard. In production, emails would be sent to your actual email address.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailDemo;